import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Accessibility,
  AlertTriangle,
  ArrowRight,
  Bed,
  PersonStanding,
  Plus,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChipRadioGroup } from "@/components/ui/chip-radio";
import { OptionTiles } from "@/components/ui/option-tiles";
import { Field } from "@/components/ui/field";
import { TRANSPORT_TYPE_LABEL } from "@/lib/tripLabels";
import { WizardCard } from "../WizardCard";
import type { TripFormValues } from "../schema";
import type { WizardStepStatus } from "../WizardCard";

interface TransportTypeStepProps {
  index: number;
  status: WizardStepStatus;
  onContinue: () => void;
  onEdit: () => void;
}

// Icon used in the chip label. Bariatric variants share the base icon
// (chair-vs-bed is the category the icon should signal, not weight
// class) but gain a small amber "+" badge in the top-right to make
// the heavier-duty variant pop visually — staff at the demo
// couldn't tell W/C from BWC at a glance without reading.
const TRANSPORT_ICON: Record<string, { icon: LucideIcon; bariatric?: boolean }> = {
  AMB: { icon: PersonStanding },
  "W/C": { icon: Accessibility },
  BWC: { icon: Accessibility, bariatric: true },
  STR: { icon: Bed },
  BSTR: { icon: Bed, bariatric: true },
};

function transportIcon(value: keyof typeof TRANSPORT_TYPE_LABEL) {
  const { icon: Icon, bariatric } = TRANSPORT_ICON[value]!;
  return (
    <span className="relative inline-flex">
      <Icon className="h-[18px] w-[18px] flex-shrink-0" aria-hidden />
      {bariatric ? (
        <span
          aria-hidden
          className="absolute -right-1.5 -top-1 inline-flex h-3 w-3 items-center justify-center rounded-full bg-amber-300 text-background ring-1 ring-background"
        >
          <Plus className="h-2 w-2" strokeWidth={4} />
        </span>
      ) : null}
    </span>
  );
}

// One line of plain English under each level, so non-clinical staff can pick
// without knowing the transport vocabulary.
const TRANSPORT_DESCRIPTION: Record<string, string> = {
  AMB: "Walks with little or no help.",
  "W/C": "Travels seated in a wheelchair.",
  BWC: "Wheelchair for patients over 300 lbs.",
  STR: "Lies flat for the whole trip.",
  BSTR: "Stretcher for patients over 300 lbs.",
};

const TRANSPORT_ORDER = ["AMB", "W/C", "BWC", "STR", "BSTR"] as const;

// =============================================================================
// TransportTypeStep
//
// Level of service. Intentionally has NO default selection — a returning
// patient may have travelled ambulatory last week and now needs a
// stretcher, so dispatch must pick fresh every time. Tap a chip and the
// wizard advances automatically.
// =============================================================================

export function TransportTypeStep({
  index,
  status,
  onContinue,
  onEdit,
}: TransportTypeStepProps) {
  const { control, setValue, formState: { errors } } =
    useFormContext<TripFormValues>();
  const value = useWatch({ control, name: "transportType" }) ?? "";
  const wheelchairSource =
    useWatch({ control, name: "wheelchairSource" }) ?? "";
  // Only regular W/C surfaces the "who brings the chair" follow-up.
  // BWC skips the question entirely — Royal can't supply bariatric
  // chairs, so the patient is always bringing their own (we set
  // wheelchairSource = "own" implicitly when BWC is picked).
  const needsWheelchairAnswer = value === "W/C";
  const isBariatricWheelchair = value === "BWC";
  const wheelchairAnswered = !needsWheelchairAnswer || Boolean(wheelchairSource);

  if (status === "complete") {
    const summaryParts = [TRANSPORT_TYPE_LABEL[value] ?? "—"];
    if (isBariatricWheelchair) {
      summaryParts.push("patient brings own");
    } else if (needsWheelchairAnswer && wheelchairSource === "own") {
      summaryParts.push("patient brings own");
    } else if (needsWheelchairAnswer && wheelchairSource === "provide") {
      summaryParts.push("dispatch provides");
    }
    return (
      <WizardCard
        index={index}
        title="Level of service"
        status="complete"
        summary={summaryParts.join(" · ")}
        onEdit={onEdit}
      />
    );
  }

  return (
    <WizardCard
      index={index}
      title="Level of service"
      description="What does the patient need today? This may differ from previous trips."
      status={status}
    >
      <Controller
        control={control}
        name="transportType"
        render={({ field }) => (
          <Field label="" error={errors.transportType?.message}>
            <OptionTiles<string>
              ariaLabel="Level of service"
              className="sm:grid-cols-2 xl:grid-cols-3"
              value={field.value ?? ""}
              onValueChange={(v) => {
                const wasEmpty = !field.value;
                field.onChange(v);
                // BWC implies patient-owned (Royal doesn't carry
                // bariatric chairs), so we set it now to keep the
                // wire payload + audit consistent without a follow-
                // up question. W/C waits for an explicit answer.
                // Any other level clears the stale source value so
                // it doesn't bleed into the notes.
                if (v === "BWC") {
                  setValue("wheelchairSource", "own", { shouldDirty: true });
                } else if (v !== "W/C") {
                  setValue("wheelchairSource", "", { shouldDirty: true });
                }
                // Auto-advance is suppressed for W/C (has the
                // patient-brings-own / provide follow-up) and BWC
                // (has the bariatric-accessibility disclosure the
                // requester needs to actually read before moving
                // on). AMB / STR / BSTR still auto-advance on first
                // pick to keep the wizard snappy.
                if (wasEmpty && v !== "W/C" && v !== "BWC") {
                  onContinue();
                }
              }}
              options={TRANSPORT_ORDER.map((v) => ({
                value: v,
                label: TRANSPORT_TYPE_LABEL[v] ?? v,
                description: TRANSPORT_DESCRIPTION[v],
                icon: transportIcon(v),
              }))}
              invalid={Boolean(errors.transportType)}
            />
          </Field>
        )}
      />

      {/* Plain-English glossary so non-clinical staff (and people new
          to medical-transport vocabulary) don't have to guess what
          "Bariatric" means. The demo audience asked specifically for
          this — the icon helps with chair-vs-bed recognition; this
          line tells you when to pick the heavier-duty variant. */}
      <p className="-mt-1 text-xs text-muted-foreground">
        <strong className="font-semibold text-foreground/80">Bariatric</strong>{" "}
        variants are for patients over 300 lbs (the regular
        wheelchair/stretcher can't be used safely above that weight).
      </p>

      {/* ─── Wheelchair source follow-up ────────────────────────────
          Folded into pickupNotes at wire time so the driver sees the
          right wheelchair line on the trip-detail screen — same
          notes channel as Companions. */}
      {needsWheelchairAnswer ? (
        <Controller
          control={control}
          name="wheelchairSource"
          render={({ field }) => (
            <Field
              label="Wheelchair"
              htmlFor="wheelchairSource"
              error={errors.wheelchairSource?.message}
              hint={
                <span className="block text-center">
                  Up to 300 lbs. Above that, switch the level of service to
                  Bariatric Wheelchair.
                </span>
              }
            >
              <ChipRadioGroup
                ariaLabel="Wheelchair source"
                value={field.value ?? ""}
                onValueChange={field.onChange}
                options={[
                  { value: "own", label: "Patient brings their own" },
                  { value: "provide", label: "Provide wheelchair" },
                ]}
                invalid={Boolean(errors.wheelchairSource)}
              />
            </Field>
          )}
        />
      ) : null}

      {/* ─── Bariatric Wheelchair: capacity disclosure ─────────────
          Royal Transportation can't supply bariatric chairs, so we
          don't ask the question — instead, flag the limitation so
          the requester knows the patient needs to bring their own.
          wheelchairSource is auto-set to "own" via the onValueChange
          handler above so the wire payload still records it. */}
      {isBariatricWheelchair ? (
        <div
          role="status"
          aria-live="polite"
          className="flex items-start gap-3 rounded-2xl border border-amber-400/45 bg-amber-400/[0.08] px-3 py-2.5"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700 dark:text-amber-300" />
          <div className="flex flex-col gap-0.5 text-sm">
            <p className="font-bold text-amber-700 dark:text-amber-200">
              Patient must bring their own bariatric wheelchair
            </p>
            <p className="text-foreground/85">
              Our vehicles are bariatric-wheelchair accessible, but Royal
              Transportation doesn't carry bariatric wheelchairs on board.
              Please confirm with the patient that they'll have their own
              chair at pickup before submitting.
            </p>
          </div>
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={onContinue}
          disabled={!value || !wheelchairAnswered}
          className="min-w-[10rem]"
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </WizardCard>
  );
}
